import { getMessaging, getToken, isSupported, onMessage, deleteToken } from 'firebase/messaging';
import app from '@config/firebase';
import { firebaseConfig, firebaseVapidKey } from '@config/environment';
import { API_ROUTES } from '@config/routes';
import { isMobileBrowser } from '@utils/isMobileBrowser';
import api from './api';

export const BROWSER_NOTIFICATION_EVENT = 'drahft:notification-received';

let cachedToken: string | null = null;
let foregroundUnsubscribe: (() => void) | null = null;
let serviceWorkerRegistrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;

/**
 * Browser push is only available on desktop browsers.
 * Mobile browsers are excluded to prevent duplicate notifications
 * with the native app (same approach Gmail uses).
 */
const isBrowserPushAvailable = () =>
  typeof window !== 'undefined' &&
  'Notification' in window &&
  'serviceWorker' in navigator &&
  window.isSecureContext &&
  !isMobileBrowser();

const getBrowserDeviceId = () => {
  const platform = navigator.platform || 'web';
  return `web-${window.location.host}-${platform}`.replace(/\s+/g, '_');
};

const getServiceWorkerScriptUrl = () => {
  const scriptUrl = new URL('/firebase-messaging-sw.js', window.location.origin);

  Object.entries(firebaseConfig).forEach(([key, value]) => {
    if (value) {
      scriptUrl.searchParams.set(key, value);
    }
  });

  return scriptUrl;
};

const ensureServiceWorkerRegistration = async () => {
  if (serviceWorkerRegistrationPromise) {
    return serviceWorkerRegistrationPromise;
  }

  serviceWorkerRegistrationPromise = navigator.serviceWorker.register(getServiceWorkerScriptUrl(), {
    scope: '/',
  });

  try {
    return await serviceWorkerRegistrationPromise;
  } catch (error) {
    serviceWorkerRegistrationPromise = null;
    console.error('[browserPush] Failed to register service worker:', error);
    return null;
  }
};

const getCurrentToken = async () => {
  if (!firebaseVapidKey || !isBrowserPushAvailable()) {
    return null;
  }

  const supported = await isSupported().catch(() => false);
  if (!supported) {
    return null;
  }

  const { permission } = window.Notification;
  if (permission !== 'granted') {
    return null;
  }

  const registration = await ensureServiceWorkerRegistration();
  if (!registration) {
    return null;
  }

  const messaging = getMessaging(app);
  return getToken(messaging, {
    vapidKey: firebaseVapidKey,
    serviceWorkerRegistration: registration,
  }).catch(error => {
    console.error('[browserPush] Failed to get web push token:', error);
    return null;
  });
};

export const registerForBrowserPushNotifications = async () => {
  if (!firebaseVapidKey || !isBrowserPushAvailable()) {
    return;
  }

  const supported = await isSupported().catch(() => false);
  if (!supported) {
    return;
  }

  let { permission } = window.Notification;
  if (permission === 'default') {
    permission = await window.Notification.requestPermission();
  }

  if (permission !== 'granted') {
    return;
  }

  const token = await getCurrentToken();
  if (!token) {
    return;
  }

  cachedToken = token;

  try {
    await api.post(API_ROUTES.NOTIFICATIONS.REGISTER_DEVICE, {
      token,
      platform: 'web',
      deviceId: getBrowserDeviceId(),
    });
  } catch (error) {
    console.error('[browserPush] Failed to register web push token:', error);
  }
};

export const unregisterBrowserPushNotifications = async () => {
  if (!isBrowserPushAvailable()) {
    return;
  }

  const supported = await isSupported().catch(() => false);
  if (!supported) {
    return;
  }

  const token = cachedToken || (await getCurrentToken());

  if (token) {
    try {
      await api.post(API_ROUTES.NOTIFICATIONS.UNREGISTER_DEVICE, { token });
    } catch (error) {
      console.error('[browserPush] Failed to unregister web push token:', error);
    }
  }

  try {
    const messaging = getMessaging(app);
    await deleteToken(messaging);
  } catch (error) {
    console.error('[browserPush] Failed to delete local web push token:', error);
  }

  cachedToken = null;
};

export const startBrowserNotificationListeners = async () => {
  if (!isBrowserPushAvailable() || foregroundUnsubscribe) {
    return;
  }

  const supported = await isSupported().catch(() => false);
  if (!supported) {
    return;
  }

  const messaging = getMessaging(app);
  foregroundUnsubscribe = onMessage(messaging, payload => {
    window.dispatchEvent(
      new CustomEvent(BROWSER_NOTIFICATION_EVENT, {
        detail: payload.data || {},
      })
    );
  });
};

export const stopBrowserNotificationListeners = () => {
  foregroundUnsubscribe?.();
  foregroundUnsubscribe = null;
};
